import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PhoneCall, UserCheck, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { User } from '../types/user'

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AuthState {
  email: string;
  phoneNumber: string;
  verificationCode: string;
  firstName: string;
  authMethod: 'email' | 'phone' | null;
  stage: 'method' | 'verification' | 'phone' | 'profile';
  isLoading: boolean;
  error: string;
  selectedCountry: Country;
  showCountrySelect: boolean;
}

interface Country {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

const countries: Country[] = [
  { code: 'CI', name: 'Côte d\'Ivoire', flag: '🇨🇮', dialCode: '+225' },
  { code: 'SN', name: 'Sénégal', flag: '🇸🇳', dialCode: '+221' },
  { code: 'BJ', name: 'Bénin', flag: '🇧🇯', dialCode: '+229' },
  { code: 'CD', name: 'RD Congo', flag: '🇨🇩', dialCode: '+243' },
  { code: 'CM', name: 'Cameroun', flag: '🇨🇲', dialCode: '+237' }
];

const defaultCountry = countries[0];

const initialState: AuthState = {
  email: '',
  phoneNumber: '',
  verificationCode: '',
  firstName: '',
  authMethod: 'email', // TODO: change to null when we have SMS verification
  stage: 'method',
  isLoading: false,
  error: '',
  selectedCountry: defaultCountry, // Côte d'Ivoire by default
  showCountrySelect: false,
};

const AuthModal = ({ isOpen, onClose }: AuthModalProps) => {
  const [state, setState] = useState<AuthState>(initialState);
  const modalRef = useRef<HTMLDivElement>(null);
  const countrySelectRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  // Handle click outside for country selector
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countrySelectRef.current && !countrySelectRef.current.contains(event.target as Node)) {
        setState(prev => ({ ...prev, showCountrySelect: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setState(initialState);
    }
  }, [isOpen]);

   if (!state.selectedCountry) {
    setState(prev => ({ ...prev, selectedCountry: defaultCountry }));
    return null; // Prevent rendering until state is initialized
  }

  const handleMethodSelect = (method: 'email' | 'phone') => {
    setState(prev => ({ ...prev, authMethod: method, error: '' }));
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const payload = {
        email: state.authMethod === 'email' ? state.email : undefined,
        phoneNumber: state.authMethod === 'phone' ? state.phoneNumber : undefined,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

      setState(prev => ({ ...prev, stage: 'verification' }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Une erreur est survenue'
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const payload = {
        email: state.email,
        phoneNumber: state.phoneNumber,
        code: state.verificationCode
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

      if (data.isNewUser && state.authMethod === 'phone') {
        setState(prev => ({ ...prev, stage: 'profile' }));
      } else if (data.isNewUser) {
        setState(prev => ({ ...prev, stage: 'phone' }));
      } else {
        localStorage.setItem('token', data.token);
        // TODO: The correct type should come from the backend 
        data.user.type = "patient"
        localStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.setItem('justLoggedIn', 'true');
        window.location.href = '/home';
        handleClose();
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Une erreur est survenue'
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const payload = {
        email: state.email,
        phoneNumber: `${state.selectedCountry.dialCode}${state.phoneNumber}`,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

    setState(prev => ({ ...prev, stage: 'profile' }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Une erreur est survenue'
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const payload = {
        email: state.authMethod === 'email' ? state.email : undefined,
        phoneNumber: state.authMethod === 'phone' ? state.phoneNumber : undefined,
        firstName: state.firstName
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

      localStorage.setItem('token', data.token);
      // TODO: The correct type should come from the backend 
      data.user.type = "patient"
      localStorage.setItem('user', JSON.stringify(data.user));
      window.location.href = '/home';
      handleClose();
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Une erreur est survenue'
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-500 
      ease-in-out flex items-center justify-center z-50">
      <div ref={modalRef}
        className={`bg-white p-8 rounded-lg shadow-xl w-96 transform transition-all 
          duration-500 ease-out ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
      >
        {state.stage === 'method' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-center" style={{color: '#00c4cc'}}>
              Connexion/Enregistrement
            </h2>
            <form onSubmit={handleRequestCode} className="space-y-4">
              <Input
                type="email"
                placeholder="votre-email@exemple.com"
                value={state.email}
                onChange={(e) => setState(prev => ({ ...prev, email: e.target.value, error: '' }))}
                className={state.error ? 'border-red-500' : ''}
                disabled={state.isLoading}
              />

              {state.error && (
                <p className="text-red-500 text-sm">{state.error}</p>
              )}

              <Button 
                type="submit"
                className="w-full text-white"
                style={{backgroundColor: '#00c4cc'}}
                disabled={state.isLoading || !state.email}
              >
                {state.isLoading ? 'Envoi en cours...' : 'Recevoir le code'}
              </Button>
            </form>
          </div>
        )}

        {state.stage === 'verification' && (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            <h2 className="text-2xl font-bold text-center" style={{color: '#00c4cc'}}>
              Vérification
            </h2>
            <p className="text-gray-600 text-center">
              Un code de vérification a été envoyé à{' '}
              <span className="font-medium">
                {state.authMethod === 'email' ? state.email : state.phoneNumber}
              </span>
            </p>
            
            <Input 
              type="text"
              placeholder="Code de vérification"
              value={state.verificationCode}
              onChange={(e) => setState(prev => ({ 
                ...prev, 
                verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6),
                error: ''
              }))}
              className={state.error ? 'border-red-500 text-center text-2xl tracking-wider' : 'text-center text-2xl tracking-wider'}
              disabled={state.isLoading}
              maxLength={6}
            />

            {state.error && (
              <p className="text-red-500 text-sm text-center">{state.error}</p>
            )}

            <Button 
              type="submit"
              className="w-full text-white"
              style={{backgroundColor: '#00c4cc'}}
              disabled={state.isLoading || state.verificationCode.length !== 6}
            >
              {state.isLoading ? 'Vérification...' : 'Continuer'}
            </Button>

            <button
              type="button"
              onClick={() => setState(prev => ({ ...prev, stage: 'method' }))}
              className="w-full text-gray-600 text-sm"
            >
              Changer de méthode de connexion
            </button>
          </form>
        )}

        {state.stage === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className="space-y-6">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-center" style={{color: '#00c4cc'}}>
                Bienvenue sur Yapadra !
              </h2>
              <p className="text-gray-600 text-center">
                Pour commencer vos consultations vocales,<br/>
                veuillez entrer votre numéro de téléphone.
              </p>
              
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative" ref={countrySelectRef}>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-24 flex items-center justify-between gap-2"
                      onClick={() => setState(prev => ({ ...prev, showCountrySelect: !prev.showCountrySelect }))}
                    >
                      <span>{state.selectedCountry.flag}</span>
                      <span>{state.selectedCountry.dialCode}</span>
                    </Button>
                    
                    {/* Country selector dropdown */}
                    {state.showCountrySelect && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-10">
                        {countries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => {
                              setState(prev => ({
                                ...prev,
                                selectedCountry: country,
                                showCountrySelect: false
                              }));
                            }}
                          >
                            <span>{country.flag}</span>
                            <span>{country.name}</span>
                            <span className="text-gray-500 ml-auto">{country.dialCode}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <Input
                    type="tel"
                    placeholder="XX XX XX XX XX"
                    value={formatPhoneNumberWithoutCode(state.phoneNumber)}
                    onChange={(e) => {
                      let newValue = e.target.value;
                      const previousValue = state.phoneNumber;

                      // Remove all non-digits from both values
                      const newDigits = newValue.replace(/\D/g, '');
                      const prevDigits = previousValue.replace(/\D/g, '');

                      // If we're deleting (backspace)
                      if (newDigits.length < prevDigits.length) {
                        // Remove the last digit
                        newValue = prevDigits.slice(0, -1);
                      } else {
                        // Only take the last entered digit and append it
                        const lastDigit = newDigits.slice(-1);
                        if (lastDigit) {
                          newValue = prevDigits + lastDigit;
                        } else {
                          newValue = prevDigits;
                        }
                      }

                      // Limit to 10 digits
                      newValue = newValue.slice(0, 10);

                      setState(prev => ({ 
                        ...prev, 
                        phoneNumber: newValue,
                        error: '' 
                      }));
                    }}
                    className={`flex-1 ${state.error ? 'border-red-500' : ''} 
                      ${isValidPhoneNumber(state.phoneNumber) ? 'border-green-500' : ''}`}
                    disabled={state.isLoading}
                  />
                </div>
                
                {state.error && (
                  <p className="text-red-500 text-sm text-center">{state.error}</p>
                )}
              </div>

              <Button 
                type="submit"
                className="w-full text-white"
                style={{backgroundColor: '#00c4cc'}}
                disabled={state.isLoading || !isValidPhoneNumber(state.phoneNumber)}
              >
                {state.isLoading ? 'Sauvegarde en cours...' : 'Continuer'}
              </Button>
            </div>
          </form>
        )}

        {state.stage === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <h2 className="text-2xl font-bold text-center" style={{color: '#00c4cc'}}>
              C'est la dernière étape de votre enregistrement!
            </h2>
            <p className="text-gray-600 text-center">
              Pour personnaliser votre expérience, veuillez nous dire comment vous vous appelez.
            </p>
            
            <Input 
              type="text"
              placeholder="Votre prénom"
              value={state.firstName}
              onChange={(e) => setState(prev => ({ ...prev, firstName: e.target.value, error: '' }))}
              className={state.error ? 'border-red-500' : ''}
              disabled={state.isLoading}
              autoFocus
            />

            {state.error && (
              <p className="text-red-500 text-sm text-center">{state.error}</p>
            )}

            <Button 
              type="submit"
              className="w-full text-white"
              style={{backgroundColor: '#00c4cc'}}
              disabled={state.isLoading || !state.firstName.trim()}
            >
              {state.isLoading ? 'Enregistrement...' : 'Commencer'}
            </Button>
          </form>
        )}

        <Button 
          onClick={handleClose}
          className="w-full mt-6"
          variant="outline"
          disabled={state.isLoading}
        >
          Continuer
        </Button>
      </div>
    </div>
  );
};

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WaitlistModal = ({ isOpen, onClose }: WaitlistModalProps) => {
  const [email, setEmail] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 500) {
          setError('Vous êtes déjà inscrit sur la liste d\'attente. Soyez patient ;)');
        } else {
          setError(data.error || 'Une erreur est survenue');
        }
        return;
      }

      setIsSubmitted(true);
    } catch (err) {
      setError('Une erreur de connexion est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black transition-opacity duration-500 ease-in-out flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className={`bg-white p-8 rounded-lg shadow-xl w-96 transform transition-all duration-500 ease-out
          ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
      >
        {!isSubmitted ? (
          <div className="animate-fadeIn">
            <h2 className="text-xl font-bold mb-4" style={{color: '#00c4cc'}}>
              Rejoignez la liste d'attente
            </h2>
            <p className="text-gray-600 mb-6">
              Notre service n'est pas encore disponible, mais nous vous informerons dès son lancement.
            </p>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}>
              <div className="space-y-2">
                <Input 
                  type="email"
                  placeholder="votre-email@exemple.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  className={`w-full ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
                {error && (
                  <p className="text-red-500 text-sm animate-fadeIn">
                    {error}
                  </p>
                )}
              </div>
              <Button 
                type="submit"
                className="w-full text-white mt-6 mb-4 transform transition-transform duration-300 hover:scale-102"
                style={{backgroundColor: '#00c4cc'}}
                disabled={isLoading || !email}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <span className="mr-2">Inscription en cours</span>
                  </span>
                ) : (
                  'Rejoindre la liste d\'attente'
                )}
              </Button>
            </form>
          </div>
        ) : (
          <div className="animate-fadeIn">
            <h2 className="text-xl font-bold mb-4" style={{color: '#00c4cc'}}>
              Merci de votre intérêt !
            </h2>
            <p className="text-gray-600 mb-6">
              Nous vous contacterons dès que notre service sera disponible.
            </p>
          </div>
        )}
        <Button 
          onClick={onClose}
          className="w-full transform transition-transform duration-300 hover:scale-102"
          variant="outline"
          disabled={isLoading}
        >
          Fermer
        </Button>
      </div>
    </div>
  );
};

const formatPhoneNumberWithoutCode = (number: string): string => {
  if (!number) return '';
  
  // Group digits in pairs
  const matches = number.match(/.{1,2}/g);
  return matches ? matches.join(' ') : number;
};

const isValidPhoneNumber = (number: string): boolean => {
  const digitsOnly = number.replace(/\D/g, '');
  return digitsOnly.length === 8 || digitsOnly.length === 10;
};

interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  shortDescription: string;
  longDescription: string;
  isExpanded: boolean;
  onClick: () => void;
}

const InfoCard = ({ icon, title, shortDescription, longDescription, isExpanded, onClick }: InfoCardProps) => {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg shadow-md text-center cursor-pointer p-6
        transform transition-all duration-500 ease-out
        hover:shadow-lg h-fit`}
    >
      <div className="flex flex-col items-center">
        <div className="mb-4" style={{color: '#00c4cc'}}>
          {icon}
        </div>
        <h3 className="font-bold mb-2" style={{color: '#00c4cc'}}>
          {title}
        </h3>
        <p className="text-gray-600 mb-2">
          {shortDescription}
        </p>
        <div 
          className={`transition-all duration-500 overflow-hidden
          ${isExpanded ? 'max-h-[1000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}
        >
          <p className="text-gray-600 px-4">
            {longDescription}
          </p>
        </div>
        <div className="mt-4" style={{color: '#00c4cc'}}>
          {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </div>
      </div>
    </div>
  );
};

interface TeleTherapyLandingProps {
  onPlanSession: () => void;
  onLogout: () => void;
  isWaitlistModalOpen: boolean;
  setIsWaitlistModalOpen: (open: boolean) => void;
}

const TeleTherapyLanding = ({ onPlanSession, onLogout, isWaitlistModalOpen, setIsWaitlistModalOpen }: TeleTherapyLandingProps) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const loadUserData = useCallback(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
      }
    }
  }, []);

  // Load user data on mount and storage changes
  useEffect(() => {
    loadUserData();

    // Listen for storage changes (in case of multiple tabs)
    window.addEventListener('storage', loadUserData);
    return () => window.removeEventListener('storage', loadUserData);
  }, [loadUserData]);

  const handleCloseAuth = () => {
    setIsAuthModalOpen(false);
    // Refresh user data when modal closes
    loadUserData();
  };

  const handleLogout = () => {
    onLogout();
    setUser(null);
  };

  const infoCards = [
    {
      icon: <PhoneCall size={48} />,
      title: "Consultations Vocales Uniquement",
      shortDescription: "Sessions confidentielles et privées via des appels vocaux sécurisés",
      longDescription: "Notre approche unique se concentre sur la voix comme moyen de connexion humaine. Sans la pression visuelle d'un appel vidéo, vous pouvez vous exprimer librement, où que vous soyez. Nos sessions vocales sont cryptées de bout en bout, garantissant votre confidentialité. La simplicité de l'appel vocal réduit également les barrières techniques et permet une meilleure accessibilité, même dans les zones à faible connexion internet."
    },
    {
      icon: <UserCheck size={48} />,
      title: "Professionnels Certifiés",
      shortDescription: "Psychologues agréés avec une expérience régionale approfondie",
      longDescription: "Chaque professionnel sur notre plateforme passe par un processus de vérification rigoureux. Nous vérifions leurs diplômes, leur expérience et leurs accréditations professionnelles. Nos psychologues sont spécialement formés pour les consultations vocales et comprennent les nuances culturelles de l'Afrique de l'Ouest. Ils suivent régulièrement des formations continues pour maintenir les plus hauts standards de qualité dans leurs services."
    },
    {
      icon: <Shield size={48} />,
      title: "Confidentialité Totale",
      shortDescription: "Votre vie privée est notre priorité. Sessions sécurisées et anonymes",
      longDescription: "La confidentialité est au cœur de notre service. Toutes les conversations sont protégées par un cryptage avancé. Nous respectons strictement le secret professionnel et les normes éthiques les plus élevées. Vos informations personnelles ne sont jamais partagées sans votre consentement explicite. Vous pouvez choisir de rester anonyme tout en bénéficiant d'un soutien professionnel de qualité."
    }
  ];

  return (
    <div className="min-h-screen bg-cyan-50 flex flex-col">
      <header className="bg-white shadow-md p-4 flex items-center justify-between relative z-10">
        <div className="flex items-center">
          <img 
            src="https://lh3.googleusercontent.com/d/1x5YkM88DY8OQcAzFA7hGtQl9V-lr1fdj"
            alt="Yapadra Health"
            className="h-12"
          />
        </div>
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <span className="text-gray-600">
                Bienvenue <span className="font-medium" style={{color: '#00c4cc'}}>{user.firstName}</span>
              </span>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="transform transition-transform duration-300 hover:scale-102"
              >
                Déconnexion
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => setIsAuthModalOpen(true)}
              style={{backgroundColor: '#00c4cc'}}
              className="text-white transform transition-transform duration-300 hover:scale-102"
            >
              Connexion/Enregistrement
            </Button>
          )}
        </div>
      </header>
      
      <main className="flex-grow container mx-auto px-4 py-12">
        <section className="text-center">
          <h2 className="text-3xl font-bold mb-4" style={{color: '#00c4cc'}}>
            Allô, Allô, et puis c'est fini!
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Consultations vocales confidentielles et accessibles, vous connectant 
            à un réseau de psychologues professionnels à travers l'Afrique de l'Ouest.
          </p>
          
          <Button 
            size="lg" 
            className="text-white transform transition-transform duration-300 hover:scale-102"
            style={{backgroundColor: '#00c4cc'}}
            onClick={onPlanSession}
          >
            Planifiez Votre Session
          </Button>
        </section>
        
        <section className="mt-16 grid md:grid-cols-3 gap-8">
          {infoCards.map((card, index) => (
            <InfoCard
              key={index}
              icon={card.icon}
              title={card.title}
              shortDescription={card.shortDescription}
              longDescription={card.longDescription}
              isExpanded={expandedCard === index}
              onClick={() => setExpandedCard(expandedCard === index ? null : index)}
            />
          ))}
        </section>
      </main>
      
      <footer className="text-white p-4 text-center relative z-10" style={{backgroundColor: '#00c4cc'}}>
        <p>© 2024 Yapadra Health - Le dialogue qui fait la différence</p>
      </footer>
  
      <div className="relative z-50">
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={handleCloseAuth}
        />
        
        <WaitlistModal
          isOpen={isWaitlistModalOpen}
          onClose={() => setIsWaitlistModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default TeleTherapyLanding;